import { mkdirSync, readFileSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';
import { flatten, type MessageTree } from 'verbaly';
import type { ResolvedConfig } from './config';
import { withLineEndings } from './eol';

export type Catalog = Record<string, string>;
export type Catalogs = Record<string, Catalog>;

// no prototype: a key is data, so toString or __proto__ must never answer from Object
export function emptyCatalog(): Catalog {
  return Object.create(null) as Catalog;
}

// a catalog handed in by a caller may be a plain object, and its prototype is not a message
export function own(catalog: Catalog, key: string): string | undefined {
  return Object.hasOwn(catalog, key) ? catalog[key] : undefined;
}

export function catalogPath(cfg: ResolvedConfig, locale: string): string {
  return join(cfg.dir, `${locale}.json`);
}

// the flat view is the shape t() sees, so every command reads a catalog the way the runtime does
export function readCatalog(cfg: ResolvedConfig, locale: string): Catalog {
  const tree = readTree(cfg, locale);
  const bad = badLeaf(tree);
  // the runtime skips this leaf with a warn; a build tool that skipped it would write the skip back
  if (bad) {
    throw new Error(
      `[verbaly] ${catalogPath(cfg, locale)} has a non-string value at "${bad}", ` +
        'catalog values are text (groups of text are fine): fix the value',
    );
  }
  return flatten(tree);
}

// dotted path of the first value that is neither text nor a group of text (an array is a bad leaf)
export function badLeaf(tree: MessageTree, prefix = ''): string | undefined {
  for (const [key, value] of Object.entries(tree)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') continue;
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return path;
    const bad = badLeaf(value, path);
    if (bad) return bad;
  }
  return undefined;
}

// corrupt catalog throws: read as empty, the next extract would wipe the translations
function readTree(cfg: ResolvedConfig, locale: string): MessageTree {
  let content: string;
  try {
    content = readFileSync(catalogPath(cfg, locale), 'utf8');
  } catch {
    return {};
  }
  const parsed = parseTree(content);
  if (parsed === undefined) {
    throw new Error(
      `[verbaly] ${catalogPath(cfg, locale)} is not valid JSON, fix or delete the file`,
    );
  }
  // an array or a bare string reads as no messages, and the next extract would write over it
  if (!isTree(parsed)) {
    throw new Error(
      `[verbaly] ${catalogPath(cfg, locale)} is not a JSON object of messages, fix or delete the file`,
    );
  }
  return parsed;
}

// the flat view of a catalog that is not on disk: an old revision read out of git is text
export function parseCatalog(content: string): Catalog | undefined {
  const tree = parseTree(content);
  return tree === undefined ? undefined : flatten(tree as MessageTree);
}

// a byte order mark is legal in the file and illegal to JSON.parse: drop it by code, never by regex
export function parseTree(content: string): unknown {
  const body = content.charCodeAt(0) === 0xfeff ? content.slice(1) : content;
  try {
    return JSON.parse(body) as unknown;
  } catch {
    return undefined;
  }
}

export function isTree(value: unknown): value is MessageTree {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function loadCatalogs(cfg: ResolvedConfig): Catalogs {
  const catalogs: Catalogs = {};
  for (const locale of cfg.locales) catalogs[locale] = readCatalog(cfg, locale);
  return catalogs;
}

// the nesting is the author's: a file written as groups is handed back as groups
function isNested(tree: MessageTree): boolean {
  return Object.values(tree).some((value) => typeof value === 'object' && value !== null);
}

// null prototype: a group named __proto__ is data here, and on a plain object it writes to Object
function group(): MessageTree {
  return Object.create(null) as MessageTree;
}

// dotted key back to its group; a path already taken by text stays flat so nothing is overwritten
function nest(catalog: Catalog): MessageTree {
  const tree = group();
  // sorted, so "hero" always lands before "hero.title" and a message is never replaced by its group
  for (const [key, message] of Object.entries(catalog).sort(([a], [b]) => (a < b ? -1 : 1))) {
    const parts = key.split('.');
    const leaf = parts[parts.length - 1]!;
    let node: MessageTree | undefined = parts.length > 1 ? tree : undefined;
    for (let i = 0; node && i < parts.length - 1; i++) {
      const next = (node[parts[i]!] ??= group());
      node = typeof next === 'object' && next !== null ? (next as MessageTree) : undefined;
    }
    if (node && typeof node[leaf] !== 'object') node[leaf] = message;
    else tree[key] = message;
  }
  return tree;
}

function sortTree(tree: MessageTree): MessageTree {
  const sorted = group();
  for (const key of Object.keys(tree).sort()) {
    const value = tree[key];
    if (typeof value === 'string') sorted[key] = value;
    else if (value !== undefined) sorted[key] = sortTree(value as MessageTree);
  }
  return sorted;
}

function isSorted(keys: string[]): boolean {
  for (let i = 1; i < keys.length; i++) if (keys[i - 1]! > keys[i]!) return false;
  return true;
}

// a level a person ordered keeps that order; one that was already sorted keeps being sorted
function orderLike(tree: MessageTree, previous: MessageTree): MessageTree {
  const had = Object.keys(previous);
  if (isSorted(had)) return sortTree(tree);

  const out = group();
  const take = (key: string): void => {
    const value = tree[key];
    if (typeof value === 'string') {
      out[key] = value;
      return;
    }
    if (value === undefined) return;
    const before = previous[key];
    out[key] =
      typeof before === 'object' && before !== null ? orderLike(value, before) : sortTree(value);
  };

  for (const key of had) if (Object.hasOwn(tree, key)) take(key);
  // a key the file did not have is an addition, and it reads as one at the end of its group
  for (const key of Object.keys(tree).sort()) if (!Object.hasOwn(previous, key)) take(key);
  return out;
}

export function serializeCatalog(catalog: Catalog, nested = false, previous?: MessageTree): string {
  const tree: MessageTree = nested ? nest(catalog) : catalog;
  return JSON.stringify(previous ? orderLike(tree, previous) : sortTree(tree), null, 2) + '\n';
}

export function writeCatalog(cfg: ResolvedConfig, locale: string, catalog: Catalog): string {
  const path = catalogPath(cfg, locale);
  let existing: string | undefined;
  try {
    existing = readFileSync(path, 'utf8');
  } catch {
    mkdirSync(cfg.dir, { recursive: true });
  }
  // parsed once: the shape and the key order are two questions about the same previous file
  const parsed = existing === undefined ? undefined : parseTree(existing);
  const previous = isTree(parsed) ? parsed : undefined;
  const serialized = withLineEndings(
    serializeCatalog(catalog, wantsNesting(cfg, locale, existing, previous), previous),
    existing,
  );
  // identical writes are skipped: a rewrite retriggers whatever watches the catalog
  if (existing === serialized) return serialized;
  writeFileSync(path, serialized);
  return serialized;
}

// the file's own shape, and for a locale that does not exist yet, the source catalog's
function wantsNesting(
  cfg: ResolvedConfig,
  locale: string,
  existing: string | undefined,
  previous: MessageTree | undefined,
): boolean {
  if (existing !== undefined) return previous !== undefined && isNested(previous);
  if (locale === cfg.sourceLocale) return false;
  return isNested(readTree(cfg, cfg.sourceLocale));
}

// the same test the runtime uses, asked of the catalogs so the parser ships only where it is read
const ICU_ARG =
  /(^|[^{])\{\s*[a-zA-Z0-9_]+\s*,\s*(?:plural|selectordinal|select|number|date|time)\b/;

export function needsIcu(catalogs: Catalogs): boolean {
  for (const catalog of Object.values(catalogs)) {
    for (const message of Object.values(flatten(catalog as MessageTree))) {
      if (ICU_ARG.test(message)) return true;
    }
  }
  return false;
}

// relative time is the one format heavy enough to be worth shipping only where it is written
const RELATIVE_ARG = /\{[^{}]*:\s*relative\b/;

export function needsRelative(catalogs: Catalogs): boolean {
  for (const catalog of Object.values(catalogs)) {
    for (const message of Object.values(flatten(catalog as MessageTree))) {
      if (RELATIVE_ARG.test(message)) return true;
    }
  }
  return false;
}
